import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Save, Loader2 } from 'lucide-react';
import { createItem, getItemById, updateItem } from '../api/itemsApi';
import { productLines } from '../data/productLines';
import { colors } from '../data/colors';
import { coatings } from '../data/coatings';

const AdminProductForm = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const isEditing = !!id;

    const [loading, setLoading] = useState(false);
    const [initialLoading, setInitialLoading] = useState(isEditing);

    const [formData, setFormData] = useState({
        nombre: '',
        precio: '',
        precioNegocio: '',
        imagen: '',
        descripcion: '',
        linea: '',
        diametro: '',
        capacidad: '',
        comensales: '',
        revestimiento: [], // Array for checkboxes
        color: [], // Array for checkboxes
        puntosEssenPlus: '',
        puntosRegulares: '',
        puntosXL: '',
        discount: '',
        discountEndDate: ''
    });

    useEffect(() => {
        if (isEditing) {
            loadProduct();
        }
    }, [id]);

    const loadProduct = useCallback(async () => {
        try {
            const product = await getItemById(id);
            if (product) {
                // Ensure color is always an array for checkboxes
                let loadedColors = [];
                if (Array.isArray(product.color)) {
                    loadedColors = product.color;
                } else if (product.color) {
                    loadedColors = [product.color];
                }

                let loadedRevestimiento = [];
                if (Array.isArray(product.revestimiento)) {
                    loadedRevestimiento = product.revestimiento;
                } else if (product.revestimiento) {
                    loadedRevestimiento = [product.revestimiento];
                }

                setFormData({
                    ...product,
                    color: loadedColors,
                    revestimiento: loadedRevestimiento,
                    // Ensure numeric fields are strings for inputs to avoid 0 vs '' issues
                    precio: product.precio || '',
                    precioNegocio: product.precioNegocio || '',
                    diametro: product.diametro || '',
                    capacidad: product.capacidad || '',
                    comensales: product.comensales || '',
                    puntosEssenPlus: product.puntosEssenPlus || '',
                    puntosRegulares: product.puntosRegulares || '',
                    puntosXL: product.puntosXL || '',
                    discount: product.discount || '',
                    discountEndDate: product.discountEndDate || ''
                });
            }
        } catch (error) {
            console.error('Error loading product:', error);
            alert('Error al cargar el producto');
            navigate('/admin');
        } finally {
            setInitialLoading(false);
        }
    }, [id, navigate]);

    useEffect(() => {
        if (isEditing) {
            loadProduct();
        }
    }, [isEditing, loadProduct]);

    const handleChange = (e) => {
        let { name, value } = e.target;

        // Eliminar puntos si el usuario pega un número con formato (ej. 123.456)
        if (['precio', 'precioNegocio'].includes(name) && typeof value === 'string') {
            value = value.replace(/\./g, '');
        }

        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleColorChange = (colorKey) => {
        setFormData(prev => {
            const currentColors = prev.color;
            if (currentColors.includes(colorKey)) {
                return { ...prev, color: currentColors.filter(c => c !== colorKey) };
            } else {
                return { ...prev, color: [...currentColors, colorKey] };
            }
        });
    };

    const handleRevestimientoChange = (coating) => {
        setFormData(prev => {
            const currentCoatings = prev.revestimiento || [];
            if (currentCoatings.includes(coating)) {
                return { ...prev, revestimiento: currentCoatings.filter(r => r !== coating) };
            } else {
                return { ...prev, revestimiento: [...currentCoatings, coating] };
            }
        });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);

        try {
            const payload = {
                ...formData,
                precio: Number(formData.precio),
                precioNegocio: Number(formData.precioNegocio) || 0,
                diametro: Number(formData.diametro) || 0,
                capacidad: Number(formData.capacidad) || 0,
                comensales: Number(formData.comensales) || 0,
                puntosEssenPlus: Number(formData.puntosEssenPlus) || 0,
                puntosRegulares: Number(formData.puntosRegulares) || 0,
                puntosXL: Number(formData.puntosXL) || 0,
                discount: Number(formData.discount) || 0,
                discountEndDate: formData.discountEndDate || null,
                // Color is already an array
            };

            if (isEditing) {
                await updateItem(id, payload);
            } else {
                await createItem(payload);
            }
            navigate('/admin');
        } catch (error) {
            console.error('Error saving product:', error);
            alert('Error al guardar el producto');
        } finally {
            setLoading(false);
        }
    };

    if (initialLoading) {
        return (
            <div className="flex justify-center items-center h-64">
                <div className="animate-spin rounded-full h-12 w-12 border-4 border-purple-600 border-t-transparent"></div>
            </div>
        );
    }

    return (
        <div className="max-w-4xl mx-auto">
            <div className="flex items-center gap-4 mb-8">
                <button
                    onClick={() => navigate('/admin')}
                    className="p-2 hover:bg-gray-100 rounded-full transition-colors"
                >
                    <ArrowLeft className="w-6 h-6 text-gray-600" />
                </button>
                <h1 className="text-3xl font-bold text-gray-900">
                    {isEditing ? 'Editar Producto' : 'Nuevo Producto'}
                </h1>
            </div>

            <form onSubmit={handleSubmit} className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 space-y-8">

                {/* Basic Info */}
                <div className="space-y-4">
                    <h2 className="text-xl font-semibold text-gray-800 border-b pb-2">Información Básica</h2>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div className="space-y-2">
                            <label className="block text-sm font-medium text-gray-700">Nombre</label>
                            <input
                                type="text"
                                name="nombre"
                                required
                                className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none"
                                value={formData.nombre}
                                onChange={handleChange}
                            />
                        </div>
                        <div className="space-y-2">
                            <label className="block text-sm font-medium text-gray-700">Línea</label>
                            <select
                                name="linea"
                                className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none"
                                value={formData.linea}
                                onChange={handleChange}
                            >
                                <option value="">Seleccionar línea</option>
                                {productLines.filter(line => line !== 'all').map(line => (
                                    <option key={line} value={line}>{line}</option>
                                ))}
                            </select>
                        </div>
                    </div>

                    <div className="space-y-2">
                        <label className="block text-sm font-medium text-gray-700">Descripción</label>
                        <textarea
                            name="descripcion"
                            rows="3"
                            className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none resize-none"
                            value={formData.descripcion}
                            onChange={handleChange}
                        ></textarea>
                    </div>

                    <div className="space-y-2">
                        <label className="block text-sm font-medium text-gray-700">Imagen URL</label>
                        <input
                            type="url"
                            name="imagen"
                            className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none"
                            value={formData.imagen}
                            onChange={handleChange}
                            placeholder="https://..."
                        />
                    </div>
                </div>

                {/* Prices */}
                <div className="space-y-4">
                    <h2 className="text-xl font-semibold text-gray-800 border-b pb-2">Precios</h2>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div className="space-y-2">
                            <label className="block text-sm font-medium text-gray-700">Precio Lista</label>
                            <input
                                type="number"
                                name="precio"
                                required
                                min="0"
                                className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none"
                                value={formData.precio}
                                onChange={handleChange}
                            />
                        </div>
                        <div className="space-y-2">
                            <label className="block text-sm font-medium text-gray-700">Precio Negocio</label>
                            <input
                                type="number"
                                name="precioNegocio"
                                min="0"
                                className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none"
                                value={formData.precioNegocio}
                                onChange={handleChange}
                            />
                        </div>
                    </div>
                </div>

                {/* Discounts */}
                <div className="space-y-4">
                    <h2 className="text-xl font-semibold text-gray-800 border-b pb-2">Descuento Especial</h2>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div className="space-y-2">
                            <label className="block text-sm font-medium text-gray-700">Porcentaje de Descuento (%)</label>
                            <input
                                type="number"
                                name="discount"
                                min="0"
                                max="100"
                                placeholder="Ej: 10"
                                className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none"
                                value={formData.discount}
                                onChange={handleChange}
                            />
                            <p className="text-xs text-gray-500 italic">Se aplicará sobre los tres precios si la fecha es válida.</p>
                        </div>
                        <div className="space-y-2">
                            <label className="block text-sm font-medium text-gray-700">Fecha de Fin</label>
                            <input
                                type="date"
                                name="discountEndDate"
                                className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none"
                                value={formData.discountEndDate}
                                onChange={handleChange}
                            />
                            <p className="text-xs text-gray-500 italic">El descuento estará activo hasta esta fecha inclusive.</p>
                        </div>
                    </div>
                </div>

                {/* Points */}
                <div className="space-y-4">
                    <h2 className="text-xl font-semibold text-gray-800 border-b pb-2">Puntos</h2>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                        <div className="space-y-2">
                            <label className="block text-sm font-medium text-gray-700">Puntos Essen Plus</label>
                            <input
                                type="number"
                                name="puntosEssenPlus"
                                min="0"
                                className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none"
                                value={formData.puntosEssenPlus}
                                onChange={handleChange}
                            />
                        </div>
                        <div className="space-y-2">
                            <label className="block text-sm font-medium text-gray-700">Puntos Regulares</label>
                            <input
                                type="number"
                                name="puntosRegulares"
                                min="0"
                                className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none"
                                value={formData.puntosRegulares}
                                onChange={handleChange}
                            />
                        </div>
                        <div className="space-y-2">
                            <label className="block text-sm font-medium text-gray-700">Puntos XL</label>
                            <input
                                type="number"
                                name="puntosXL"
                                min="0"
                                className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none"
                                value={formData.puntosXL}
                                onChange={handleChange}
                            />
                        </div>
                    </div>
                </div>

                {/* Technical Specs */}
                <div className="space-y-4">
                    <h2 className="text-xl font-semibold text-gray-800 border-b pb-2">Especificaciones Técnicas</h2>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        <div className="space-y-2">
                            <label className="block text-sm font-medium text-gray-700">Diámetro (cm)</label>
                            <input
                                type="number"
                                name="diametro"
                                className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none"
                                value={formData.diametro}
                                onChange={handleChange}
                            />
                        </div>
                        <div className="space-y-2">
                            <label className="block text-sm font-medium text-gray-700">Capacidad (L)</label>
                            <input
                                type="number"
                                name="capacidad"
                                step="0.1"
                                className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none"
                                value={formData.capacidad}
                                onChange={handleChange}
                            />
                        </div>
                        <div className="space-y-2">
                            <label className="block text-sm font-medium text-gray-700">Comensales</label>
                            <input
                                type="number"
                                name="comensales"
                                className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none"
                                value={formData.comensales}
                                onChange={handleChange}
                            />
                        </div>
                        <div className="space-y-2 col-span-2">
                            <label className="block text-sm font-medium text-gray-700">Revestimiento</label>
                            <div className="grid grid-cols-2 gap-3 p-1">
                                {coatings.map(coating => (
                                    <label key={coating} className="flex items-center gap-2 p-2 border rounded-lg cursor-pointer hover:bg-gray-50 transition-colors">
                                        <input
                                            type="checkbox"
                                            checked={formData.revestimiento?.includes(coating)}
                                            onChange={() => handleRevestimientoChange(coating)}
                                            className="w-4 h-4 text-purple-600 rounded focus:ring-purple-500"
                                        />
                                        <span className="text-sm text-gray-700">{coating}</span>
                                    </label>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>

                {/* Colors */}
                <div className="space-y-4">
                    <h2 className="text-xl font-semibold text-gray-800 border-b pb-2">Colores Disponibles</h2>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        {Object.keys(colors).filter(c => c !== 'all').map(colorKey => (
                            <label key={colorKey} className="flex items-center gap-3 p-3 border rounded-lg cursor-pointer hover:bg-gray-50 transition-colors">
                                <input
                                    type="checkbox"
                                    checked={formData.color.includes(colorKey)}
                                    onChange={() => handleColorChange(colorKey)}
                                    className="w-5 h-5 text-purple-600 rounded focus:ring-purple-500"
                                />
                                <div className="flex items-center gap-2">
                                    <div
                                        className="w-6 h-6 rounded-full border border-gray-200 shadow-sm"
                                        style={{ backgroundColor: colors[colorKey].hex }}
                                    ></div>
                                    <span className="text-gray-700">{colors[colorKey].name}</span>
                                </div>
                            </label>
                        ))}
                    </div>
                </div>

                <div className="pt-4 flex justify-end gap-4 border-t border-gray-100">
                    <button
                        type="button"
                        onClick={() => navigate('/admin')}
                        className="px-6 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 font-medium transition-colors"
                    >
                        Cancelar
                    </button>
                    <button
                        type="submit"
                        disabled={loading}
                        className="px-6 py-2 rounded-lg bg-purple-600 text-white hover:bg-purple-700 font-medium transition-colors flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        {loading ? (
                            <>
                                <Loader2 className="w-5 h-5 animate-spin" />
                                Guardando...
                            </>
                        ) : (
                            <>
                                <Save className="w-5 h-5" />
                                Guardar Producto
                            </>
                        )}
                    </button>
                </div>
            </form>
        </div>
    );
};

export default AdminProductForm;
